import { createContext, useContext, useEffect, useState, useCallback } from 'react'

import { Map, View, type MapBrowserEvent } from 'ol'
import { inAndOut } from 'ol/easing'
import TileLayer from 'ol/layer/Tile'
import { fromLonLat, toLonLat } from 'ol/proj'
import XYZ from 'ol/source/XYZ'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MapContextProps {
  map: Map | undefined
  activeLayer: string
  changeActiveLayer: (layer: string) => void
  setInitialZoom: (zoom: number) => void
}

export interface MapProviderProps {
  children: React.ReactNode
  /** Initial map center as [lon, lat]. Default: [0, 20] */
  center?: [number, number]
  /** Initial zoom level. Default: 5 */
  initialZoom?: number
  /** Called on every map single-click (when no draw tool is active) */
  onMapClick?: (params: { lng: number; lat: number; coordinate: number[]; pixel: number[] }) => void
  /** Called on every map move-end */
  onMapMove?: (params: { lng: number; lat: number; zoom: number }) => void
  /**
   * Basemap tile URL template. Defaults to Esri satellite imagery.
   * Use any XYZ tile URL, e.g. OSM: 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png'
   * Set to null to disable the default basemap entirely.
   */
  basemapUrl?: string | null
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const MapContext = createContext<MapContextProps | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const DEFAULT_BASEMAP_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

export const MapProvider = ({
  children,
  center = [0, 20],
  initialZoom,
  onMapClick,
  onMapMove,
  basemapUrl = DEFAULT_BASEMAP_URL,
}: MapProviderProps) => {
  const [mapInstance, setMapInstance] = useState<Map | undefined>(undefined)
  const [activeLayer, setActiveLayer] = useState('')
  const [configuredZoom, setConfiguredZoom] = useState<number | undefined>(initialZoom)

  const zoomLevel = configuredZoom ?? 5

  const handleSetInitialZoom = useCallback((zoom: number) => {
    setConfiguredZoom(zoom)
  }, [])

  useEffect(() => {
    const newMap = new Map({
      view: new View({
        center: fromLonLat(center),
        zoom: 3,
        maxZoom: 24,
        constrainResolution: false,
      }),
      controls: [],
      // Performance: pixelRatio:1 reduces paint times on high-DPI screens
      pixelRatio: 1,
    })

    // Add default basemap so the map is visible without any LayerSwitcher component
    if (basemapUrl) {
      const basemap = new TileLayer({
        source: new XYZ({
          url: basemapUrl,
          crossOrigin: 'anonymous',
          cacheSize: 512,
          maxZoom: 19,
        }),
        zIndex: 0,
        preload: 2,
        useInterimTilesOnError: true,
      })
      basemap.set('type', 'basemap')
      basemap.set('id', 'basemap-layer')
      newMap.addLayer(basemap)
    }

    setMapInstance(newMap)

    // Global click handler — skips when a draw interaction is active
    const handleMapClick = (evt: MapBrowserEvent<PointerEvent>) => {
      const interactions = newMap.getInteractions().getArray()
      const drawActive = interactions.some(
        (interaction) =>
          (interaction.get('id') === 'draw' || interaction.get('id') === 'measure-interaction') &&
          interaction.getActive(),
      )

      if (drawActive) {
        return
      }

      const lonLat = toLonLat(evt.coordinate)
      if (lonLat && lonLat.length >= 2) {
        const lng = lonLat[0]!
        const lat = lonLat[1]!
        onMapClick?.({ lng, lat, coordinate: evt.coordinate, pixel: evt.pixel })
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - OpenLayers type mismatch
    newMap.on('singleclick', handleMapClick)

    const handleMoveEnd = () => {
      const view = newMap.getView()
      const mapCenter = view.getCenter()
      const zoom = view.getZoom() || 0

      if (mapCenter) {
        const lonLat = toLonLat(mapCenter)
        onMapMove?.({ lng: lonLat[0]!, lat: lonLat[1]!, zoom })
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - OpenLayers type mismatch
    newMap.on('moveend', handleMoveEnd)

    // Animate to initial position
    setTimeout(() => {
      newMap.getView().animate({
        center: fromLonLat(center),
        zoom: zoomLevel,
        duration: 1400,
        easing: inAndOut,
      })
    }, 50)
  }, [zoomLevel])

  const changeActiveLayer = useCallback((layerId: string) => {
    setActiveLayer(layerId)
  }, [])

  return (
    <MapContext.Provider
      value={{
        map: mapInstance,
        activeLayer,
        changeActiveLayer,
        setInitialZoom: handleSetInitialZoom,
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useMap = () => {
  const context = useContext(MapContext)
  if (context === undefined) {
    throw new Error('useMap must be used inside <MapProvider>')
  }
  return context
}
